import React, { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import TabPageLayout, { PageLoader, DataCard, EmptyState } from './TabPageLayout';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Grid3x3, Calendar, MapPin, Link, Download, QrCode, Copy, Workflow, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { BACKEND_URL, WIDGET_PRODUCTION_URL, PRODUCTION_BACKEND_URL } from '../config/api';
import api from '../utils/api';

const FlowsTab = () => {
  // Get organization ID from localStorage
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const organizationId = userData.organizationId || localStorage.getItem('organizationId');
  const orgSlug = localStorage.getItem('organizationSlug');
  const orgName = localStorage.getItem('organizationName') || 'BOATX';
  
  // State
  const [selectedWidget, setSelectedWidget] = useState('service-grid');
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [enableCustomRequests, setEnableCustomRequests] = useState(true);
  const qrCodeRef = useRef(null);
  
  // URLs from centralized config
  const backendUrl = BACKEND_URL;
  const previewBaseUrl = window.location.origin;
  const productionWidgetUrl = WIDGET_PRODUCTION_URL;
  const productionBackendUrl = PRODUCTION_BACKEND_URL;
  
  // Fetch widget config to get custom requests toggle state
  useEffect(() => {
    const fetchWidgetConfig = async () => {
      try {
        const response = await api.get('/widget/config');
        if (response.data) {
          setEnableCustomRequests(response.data.enableCustomRequests !== false);
        }
      } catch (error) {
        console.log('Widget config not found, using defaults');
      }
    };
    fetchWidgetConfig();
  }, []);

  // Save custom requests toggle
  const handleCustomRequestsToggle = async (checked) => {
    setEnableCustomRequests(checked);
    try {
      await api.put('/widget/config', { enableCustomRequests: checked });
      toast.success(checked ? 'Custom requests enabled' : 'Custom requests disabled');
    } catch (error) {
      console.error('Error saving widget config:', error);
      toast.error('Failed to save setting');
      setEnableCustomRequests(!checked); // Revert on error
    }
  };
  
  // Fetcher for SWR
  const fetcher = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch');
    return response.json();
  };
  
  // Use SWR for token fetching
  const { data: tokenData, error: tokenError, isLoading, mutate } = useSWR(
    orgSlug ? `${backendUrl}/api/embed-tokens/current/${orgSlug}` : null,
    fetcher,
    { revalidateOnFocus: true }
  );
  
  const embedToken = tokenData?.exists ? tokenData.token : '';
  
  // Validate token
  const { data: validationData } = useSWR(
    embedToken ? `${backendUrl}/api/public/services?token=${embedToken}` : null,
    fetcher,
    { revalidateOnFocus: false }
  );
  
  const tokenValid = validationData?.services !== undefined;

  // Generate embed token
  const generateEmbedToken = async () => {
    if (!orgSlug) {
      toast.error('Organization not found');
      return;
    }
    
    setIsGenerating(true);
    try {
      const response = await fetch(`${backendUrl}/api/embed-tokens/generate/${orgSlug}`);
      
      if (!response.ok) throw new Error('Failed to generate token');
      
      const data = await response.json();
      
      // Store token and revalidate SWR
      localStorage.setItem(`embedToken_${organizationId}`, data.token);
      mutate();
      
      // Mark Flows as complete
      try {
        const token = localStorage.getItem('jwtToken');
        await fetch(`${backendUrl}/api/organizations/mark-flows-complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
            ...(orgSlug && { 'X-Organization-Slug': orgSlug })
          }
        });
        window.dispatchEvent(new Event('setupDataChanged'));
      } catch (err) {
        console.error('Failed to mark flows complete:', err);
      }
      
      toast.success('Secure embed token generated');
    } catch (error) {
      console.error('Error generating embed token:', error);
      toast.error('Failed to generate token');
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle regenerate token flow
  const handleRegenerateToken = () => setShowRegenerateModal(true);
  
  const handleFirstConfirm = () => {
    setShowRegenerateModal(false);
    setConfirmText('');
    setShowFinalConfirm(true);
  };
  
  const handleFinalConfirm = () => {
    if (confirmText.toLowerCase() === 'new token') {
      setShowFinalConfirm(false);
      setConfirmText('');
      generateEmbedToken();
    }
  };
  
  const handleCancel = () => {
    setShowRegenerateModal(false);
    setShowFinalConfirm(false);
    setConfirmText('');
  };

  // QR Code functions
  const downloadQRCode = () => {
    const canvas = qrCodeRef.current?.querySelector('canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.download = `${orgName}-booking-qr-code.png`;
      link.href = url;
      link.click();
      toast.success('QR Code downloaded!');
    }
  };

  const copyToClipboard = async (text, message = 'Copied to clipboard!') => {
    await navigator.clipboard.writeText(text);
    toast.success(message);
    
    // Mark Flows as complete
    try {
      const token = localStorage.getItem('jwtToken');
      await fetch(`${backendUrl}/api/organizations/mark-flows-complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...(orgSlug && { 'X-Organization-Slug': orgSlug })
        }
      });
      window.dispatchEvent(new Event('setupDataChanged'));
    } catch (err) {
      console.error('Failed to mark flows complete:', err);
    }
  };

  // Handle iframe messages for preview
  useEffect(() => {
    let modalOverlay = null;
    
    const handleMessage = (e) => {
      if (e.data.type === 'RESIZE_IFRAME') {
        const iframe = document.getElementById('preview-iframe');
        if (iframe) iframe.style.height = e.data.height + 'px';
      }
      
      if (e.data.type === 'OPEN_BOOKING_MODAL' && embedToken) {
        // Create modal overlay for booking
        if (!modalOverlay) {
          modalOverlay = document.createElement('div');
          modalOverlay.className = 'fixed inset-0 bg-black/60 backdrop-blur-sm z-[999999] flex items-center justify-center p-5';
          
          const modalIframe = document.createElement('iframe');
          modalIframe.className = 'w-full max-w-[850px] h-[90vh] border-none rounded-xl bg-white';
          modalIframe.src = `${previewBaseUrl}/services-grid.html?token=${embedToken}&fullscreen=true&service=${e.data.serviceId}&api=${encodeURIComponent(backendUrl)}`;
          
          modalOverlay.appendChild(modalIframe);
          document.body.appendChild(modalOverlay);
          document.body.style.overflow = 'hidden';
        }
      }
      
      if (e.data.type === 'CLOSE_BOOKING_MODAL' && modalOverlay) {
        modalOverlay.remove();
        modalOverlay = null;
        document.body.style.overflow = '';
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (modalOverlay) modalOverlay.remove();
    };
  }, [embedToken, previewBaseUrl, backendUrl]);

  // Build widget configs
  const effectiveToken = embedToken || btoa(organizationId || '');
  const paramName = embedToken ? 'token' : 'org';
  const paramValue = effectiveToken;

  const widgets = [
    {
      id: 'service-grid',
      name: 'Services First',
      icon: Grid3x3,
      description: 'Show all services in a grid layout',
      previewUrl: `${previewBaseUrl}/services-grid.html?${paramName}=${paramValue}&embed=true&preview=true&api=${encodeURIComponent(backendUrl)}`,
      embedCode: `<script src="${productionWidgetUrl}/embeds/calendar.js?${paramName}=${paramValue}&view=services&api=${encodeURIComponent(productionBackendUrl)}"></script>`
    },
    {
      id: 'calendar-first',
      name: 'Calendar First',
      icon: Calendar,
      description: 'Start with date picker, then show services',
      previewUrl: null,
      embedCode: `${productionWidgetUrl}/widget-calendar.html?org_id=${organizationId}`
    },
    {
      id: 'book-now-button',
      name: 'Floating Button',
      icon: MapPin,
      description: 'Sticky button that opens booking in modal',
      previewUrl: null,
      embedCode: `<script src="${productionWidgetUrl}/embeds/button.js?${paramName}=${paramValue}&api=${encodeURIComponent(productionBackendUrl)}"></script>`
    },
    {
      id: 'direct-link',
      name: 'Direct Link',
      icon: Link,
      description: 'Share on Instagram, TikTok, or any social platform',
      previewUrl: null,
      embedCode: `${productionWidgetUrl}/book.html?${paramName}=${paramValue}&api=${encodeURIComponent(productionBackendUrl)}`
    }
  ];

  const selectedWidgetData = widgets.find(w => w.id === selectedWidget);

  // Render code with syntax highlighting
  const renderCodeWithColors = (code) => {
    if (!code.includes('<')) {
      return <span className="text-cyan-400">{code}</span>;
    }

    const scriptMatch = code.match(/<script\s+src="([^"]+)"><\/script>/);
    if (scriptMatch) {
      const urlParts = scriptMatch[1].split('?');
      const baseUrl = urlParts[0];
      const tokenMatch = urlParts[1]?.match(/token=([^&]+)/);
      const viewMatch = urlParts[1]?.match(/view=([^&]+)/);
      
      return (
        <span>
          <span className="text-pink-500">&lt;script </span>
          <span className="text-green-400">src</span>
          <span className="text-gray-400">=</span>
          <span className="text-blue-400">&quot;</span>
          <span className="text-blue-300">{baseUrl}</span>
          {tokenMatch && (
            <>
              <span className="text-purple-400">?token=</span>
              <span className="text-teal-400">{tokenMatch[1]}</span>
            </>
          )}
          {viewMatch && (
            <>
              <span className="text-purple-400">&amp;view=</span>
              <span className="text-yellow-400">{viewMatch[1]}</span>
            </>
          )}
          <span className="text-blue-400">&quot;</span>
          <span className="text-pink-500">&gt;&lt;/script&gt;</span>
        </span>
      );
    }
    
    return <span className="text-gray-300">{code}</span>;
  };

  // Loading state
  if (isLoading) {
    return (
      <TabPageLayout title="Booking Flows">
        <PageLoader />
      </TabPageLayout>
    );
  }

  // Error state
  if (tokenError) {
    return (
      <TabPageLayout title="Booking Flows">
        <EmptyState
          icon={<AlertCircle />}
          title="Failed to load"
          description="Could not load your embed token. Please refresh the page."
          action={
            <Button onClick={() => mutate()} className="bg-blue-600 hover:bg-blue-700 text-white">
              Retry
            </Button>
          }
        />
      </TabPageLayout>
    );
  }

  // No organization
  if (!organizationId) {
    return (
      <TabPageLayout title="Booking Flows">
        <EmptyState
          icon={<Workflow />}
          title="Organization not found"
          description="Please refresh the page to continue."
        />
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Booking Flows"
      subtitle="Embed booking flows on your website to let customers book directly"
    >
      <div className="space-y-6">
        {/* Flow Type Selector */}
        <DataCard title="Choose Your Flow" subtitle="Select a flow type and paste one line of code into your website">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {widgets.map((widget) => {
              const Icon = widget.icon;
              return (
                <button
                  key={widget.id}
                  onClick={() => setSelectedWidget(widget.id)}
                  className={`p-4 border rounded-xl text-left transition-all hover:scale-[1.02] ${
                    selectedWidget === widget.id
                      ? 'border-blue-600 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <Icon className="w-7 h-7 mb-2 text-gray-700" />
                  <div className="font-semibold text-sm text-gray-900 mb-1">{widget.name}</div>
                  <div className="text-xs text-gray-500">{widget.description}</div>
                </button>
              );
            })}
          </div>
        </DataCard>

        {/* Embed Code Section */}
        {selectedWidgetData && (
          <DataCard 
            title="Embed Code" 
            subtitle="Copy and paste this into your website's HTML"
            actions={
              embedToken && (
                <Button
                  size="sm"
                  onClick={() => copyToClipboard(selectedWidgetData.embedCode, 'Code copied!')}
                  className="bg-blue-600 hover:bg-blue-700 text-white h-9"
                  disabled={selectedWidget === 'book-now-button'}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Code
                </Button>
              )
            }
          >
            {embedToken ? (
              <div className="space-y-3">
                {/* Token Status */}
                <div className="flex items-center gap-1.5">
                  {tokenValid ? (
                    <>
                      <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-xs text-green-700 font-medium">Token Active</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="text-xs text-amber-700 font-medium">Validating...</span>
                    </>
                  )}
                </div>
                
                {/* Code Block */}
                <div className={`bg-gray-900 text-gray-100 p-4 rounded-xl font-mono text-sm overflow-x-auto ${selectedWidget === 'book-now-button' ? 'opacity-50' : ''}`}>
                  <pre className="leading-relaxed">
                    {renderCodeWithColors(selectedWidgetData.embedCode)}
                  </pre>
                </div>
                
                {/* Help Text */}
                <p className="text-xs text-gray-500 leading-relaxed">
                  Paste this {selectedWidget === 'direct-link' ? 'link' : 'code'} into your website. 
                  Need to revoke access?{' '}
                  <button 
                    onClick={handleRegenerateToken}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Regenerate token
                  </button>
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                <p className="text-sm text-gray-700 mb-4">No embed token generated yet</p>
                <Button
                  onClick={generateEmbedToken}
                  disabled={isGenerating}
                  className="bg-blue-600 hover:bg-blue-700 text-white h-10"
                >
                  {isGenerating ? 'Generating...' : 'Generate Embed Token'}
                </Button>
                <p className="text-xs text-gray-500 mt-3">
                  Generate a secure token to enable widget embeds
                </p>
              </div>
            )}
          </DataCard>
        )}

        {/* Live Preview */}
        {selectedWidgetData?.previewUrl && (
          <DataCard title="Live Preview" subtitle="This is how your booking flow will look on your website">
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <iframe
                src={selectedWidgetData.previewUrl}
                className="w-full"
                style={{ minHeight: '600px', border: 'none' }}
                title="Booking Flow Preview"
                id="preview-iframe"
              />
            </div>
          </DataCard>
        )}

        {/* Widget Settings - Below Preview */}
        <DataCard title="Widget Settings" subtitle="Customize how your booking widget behaves">
          <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="font-semibold text-sm text-gray-900">Custom Booking Requests</div>
                <div className="text-xs text-gray-600 mt-0.5">
                  {enableCustomRequests 
                    ? 'Customers can request custom bookings from your widget'
                    : 'Custom booking requests are disabled'}
                </div>
              </div>
            </div>
            <Switch
              checked={enableCustomRequests}
              onCheckedChange={handleCustomRequestsToggle}
            />
          </div>
          
          {enableCustomRequests && (
            <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 border border-gray-200 mt-3">
              <span className="font-medium text-gray-900">How it works:</span> A "Don't see what you're looking for?" link appears at the bottom of your widget. When customers click it, they can describe what they need and submit a request that goes to your Relations tab.
            </div>
          )}
        </DataCard>

        {/* No Preview State */}
        {selectedWidgetData && !selectedWidgetData.previewUrl && (
          <DataCard>
            <div className="py-8 text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600">
                {selectedWidget === 'book-now-button' ? (
                  <MapPin className="w-8 h-8 text-white" />
                ) : selectedWidget === 'calendar-first' ? (
                  <Calendar className="w-8 h-8 text-white" />
                ) : (
                  <Link className="w-8 h-8 text-white" />
                )}
              </div>
              
              {selectedWidget === 'book-now-button' || selectedWidget === 'calendar-first' ? (
                <>
                  <p className="text-lg font-semibold text-gray-900 mb-2">Coming Soon...</p>
                  <p className="text-sm text-gray-600">
                    {selectedWidget === 'book-now-button' 
                      ? 'Floating button will appear in the bottom-right corner'
                      : 'Calendar-first booking flow'}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Share on Instagram, TikTok, email, or SMS
                  </p>
                  
                  {embedToken && (
                    <div className="space-y-3 max-w-md mx-auto">
                      <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-2">
                        <div className="flex-1 font-mono text-xs text-gray-600 truncate">
                          {selectedWidgetData.embedCode.length > 40 
                            ? `${selectedWidgetData.embedCode.substring(0, 22)}...${selectedWidgetData.embedCode.slice(-12)}`
                            : selectedWidgetData.embedCode}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(selectedWidgetData.embedCode, 'Link copied!')}
                          className="h-8"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </Button>
                      </div>
                      
                      <Button
                        variant="outline"
                        onClick={() => setShowQRModal(true)}
                        className="h-10"
                      >
                        <QrCode className="w-4 h-4 mr-2" />
                        Get QR Code
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </DataCard>
        )}
      </div>

      {/* First Confirmation Modal */}
      {showRegenerateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleCancel}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Regenerate Embed Token?</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  This will make any old versions of your embed code stop working.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button onClick={handleCancel} variant="outline" className="flex-1 h-10">
                Cancel
              </Button>
              <Button onClick={handleFirstConfirm} className="flex-1 h-10 bg-amber-600 hover:bg-amber-700 text-white">
                I Understand
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Final Confirmation Modal */}
      {showFinalConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleCancel}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Regeneration</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  All embed codes and QR codes will stop working. Type <strong className="text-gray-900">new token</strong> to confirm.
                </p>
                
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="new token"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button onClick={handleCancel} variant="outline" className="flex-1 h-10">
                Cancel
              </Button>
              <Button
                onClick={handleFinalConfirm}
                disabled={confirmText.toLowerCase() !== 'new token'}
                className="flex-1 h-10 bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-300"
              >
                Regenerate
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedWidget === 'direct-link' && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowQRModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl mb-4">
                <QrCode className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Download QR Code</h3>
              <p className="text-sm text-gray-600">High-resolution QR code ready for printing</p>
            </div>
            
            <div className="bg-white border-4 border-gray-100 rounded-xl p-6 mb-6 flex items-center justify-center" ref={qrCodeRef}>
              <QRCodeCanvas 
                value={selectedWidgetData?.embedCode || ''}
                size={260}
                level="H"
                includeMargin={true}
              />
            </div>
            
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-2 mb-4">
              <div className="flex-1 font-mono text-xs text-gray-600 truncate">
                {selectedWidgetData?.embedCode}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(selectedWidgetData?.embedCode || '', 'URL copied!')}
                className="h-8"
              >
                <Copy className="w-3 h-3" />
              </Button>
            </div>
            
            <div className="flex gap-3">
              <Button onClick={() => setShowQRModal(false)} variant="outline" className="flex-1 h-10">
                Close
              </Button>
              <Button onClick={downloadQRCode} className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white">
                <Download className="w-4 h-4 mr-2" />
                Download PNG
              </Button>
            </div>
            
            <p className="text-xs text-center text-gray-500 mt-4">
              Saved as: <span className="font-medium">{orgName}-booking-qr-code.png</span>
            </p>
          </div>
        </div>
      )}
    </TabPageLayout>
  );
};

export default FlowsTab;
